import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { stripMarkersDeep } from '@/utils/content-markers';

export interface OrganizationUrl {
  name: string;
  url: string;
}

export interface TherapyModality {
  id: string;
  name_en: string;
  name_cn: string;
  abbreviation: string | null;
  mechanism_en: string | null;
  mechanism_cn: string | null;
  procedures_cn: string | null;
  procedures_en: string | null;
  certifications: string[] | null;
  organizations: string[] | null;
  accredited_by: string[] | null;
  evidence_level: string | null;
  evidence_level_cn: string | null;
  stream_type: string | null;
  stream_type_cn: string | null;
  type: string | null;
  type_cn: string | null;
  subcategories: string[] | null;
  parent_therapy_id: string | null;
  organization_urls: Json | null;
  psych_condition_links: Json | null;
  // Narrative sections (v2.2 wiki structure)
  origin_narrative: string | null;
  origin_narrative_en: string | null;
  origin_century: string | null;
  population_prose: string | null;
  population_prose_en: string | null;
  contraindication_prose: string | null;
  contraindication_prose_en: string | null;
  contraindication_flags: string[] | null;
  // Age suitability (structured)
  min_age: number | null;
  max_age: number | null;
  age_groups: string[] | null;
  age_notes: string | null;
  age_notes_en: string | null;
  requires_guardian_consent: boolean;
  // Teaching layer (what to expect walking into a session)
  key_claim_cn: string | null;
  key_claim_en: string | null;
  core_concepts: Json | null;
  core_concepts_en: Json | null;
  first_session_prose: string | null;
  first_session_en: string | null;
  misconceptions: Json | null;
  misconceptions_en: Json | null;
  teaching_status: string | null;
  // Teen mode (14+) governance
  teen_status: string | null;
  teen_exclusion_reason_cn: string | null;
  teen_exclusion_reason_en: string | null;


  format_en?: string | null;
  references_json?: Json | null;
  practitioner_guidance_prose: string | null;
  practitioner_guidance_prose_en: string | null;
  // Structured ranges
  session_length_min: number | null;
  session_length_max: number | null;
  recommended_sessions_min: number | null;
  recommended_sessions_max: number | null;
  cost_per_session_min: number | null;
  cost_per_session_max: number | null;
  frequency_per_week: string | null;
  format: string | null;
  chata_stage: string | null;
  // Lineage
  derived_from: string[] | null;
  developed_into: string[] | null;
  contrasts_with: string[] | null;
  synergistic_with: string[] | null;
  is_parent_category: boolean;
  stage_0: boolean;
  stage_1: boolean;
  stage_2: boolean;
  stage_3: boolean;
  stage_4: boolean;
  is_active: boolean;
  sort_order: number;
  requires_physical_contact: boolean;
  // Domain booleans
  body: boolean;
  vitality: boolean;
  consciousness: boolean;
  context: boolean;
  // Subcategory booleans
  cognitive_consciousness: boolean;
  subconscious_patterns: boolean;
  emotional_support: boolean;
  energy_harmonization: boolean;
  bodily_regulation: boolean;
  social_relationships: boolean;
}

// Helper to parse organization_urls safely
export const parseOrganizationUrls = (urls: Json | null): OrganizationUrl[] => {
  if (!urls || !Array.isArray(urls)) return [];
  return urls
    .filter((item): item is { name: string; url: string; [key: string]: Json } => 
      typeof item === 'object' && 
      item !== null && 
      'name' in item && 
      'url' in item &&
      typeof (item as Record<string, unknown>).name === 'string' &&
      typeof (item as Record<string, unknown>).url === 'string'
    )
    .map(item => ({ name: item.name as string, url: item.url as string }));
};

export interface HealingStage {
  id: number;
  stage_number: number;
  title_en: string | null;
  title_zh: string | null;
  subtitle_en: string | null;
  subtitle_zh: string | null;
  description: string | null;
  color_theme: string | null;
  is_active: boolean | null;
}

export const useTherapyModalities = () => {
  return useQuery({
    queryKey: ['therapy-modalities'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('therapy_wiki')
        .select('*')
        .eq('is_active', true)
        .eq('qa_seed', false)
        .eq('is_parent_category', false)
        .order('sort_order', { ascending: true })
        .order('name_en', { ascending: true });

      if (error) throw error;
      // Strip invisible provenance markers before anything renders or reaches a model.
      return stripMarkersDeep(data) as TherapyModality[];
    },
    staleTime: 15 * 60 * 1000, // 15 min - static reference data
    gcTime: 30 * 60 * 1000,
  });
};

export const useHealingStages = () => {
  return useQuery({
    queryKey: ['healing-stages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('healing_stages')
        .select('*')
        .eq('is_active', true)
        .order('stage_number', { ascending: true });

      if (error) throw error;
      return data as HealingStage[];
    },
  });
};

// Get therapies grouped by stage
export const getTherapiesByStage = (therapies: TherapyModality[], stageNumber: number): TherapyModality[] => {
  const stageKey = `stage_${stageNumber}` as keyof TherapyModality;
  return therapies.filter(t => t[stageKey] === true);
};

// Get unique evidence levels
export const getEvidenceLevels = (therapies: TherapyModality[]): string[] => {
  const levels = new Set<string>();
  therapies.forEach(t => {
    if (t.evidence_level) levels.add(t.evidence_level);
  });
  return Array.from(levels).sort();
};

// Get unique stream types
export const getStreamTypes = (therapies: TherapyModality[]): string[] => {
  const types = new Set<string>();
  therapies.forEach(t => {
    if (t.stream_type) types.add(t.stream_type);
  });
  return Array.from(types).sort();
};

export interface StreamTypeOption {
  value: string;
  labelCn: string | null;
}

// Get unique stream types with their Chinese labels (value stays the canonical English)
export const getStreamTypeOptions = (therapies: TherapyModality[]): StreamTypeOption[] => {
  const map = new Map<string, string | null>();
  therapies.forEach(t => {
    if (!t.stream_type) return;
    const existing = map.get(t.stream_type);
    if (!existing) map.set(t.stream_type, t.stream_type_cn ?? null);
  });
  return Array.from(map.entries())
    .map(([value, labelCn]) => ({ value, labelCn }))
    .sort((a, b) => a.value.localeCompare(b.value));
};

// Pick the field matching the active language, falling back to the other language
export const pickLang = <T,>(isZh: boolean, zh: T | null | undefined, en: T | null | undefined): T | null => {
  const primary = isZh ? zh : en;
  const secondary = isZh ? en : zh;
  return (primary ?? secondary ?? null) as T | null;
};
